import React from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";

export interface GradientToggleOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface GradientToggleButtonGroupProps {
  options: GradientToggleOption[];
  value: string;
  onChange: (event: React.MouseEvent<HTMLElement>, newValue: string) => void;
}

const GradientToggleButtonGroup: React.FC<GradientToggleButtonGroupProps> = ({
  options,
  value,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        overflowX: "auto",
        scrollBehavior: "smooth",
        paddingBottom: "4px", // spacing for touch scroll
        WebkitOverflowScrolling: "touch", // for iOS momentum scroll

        // Hide scrollbars
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          width: "max-content",
        }}
      >
        <ToggleButtonGroup
          value={value}
          exclusive
          onChange={onChange}
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: "48px !important",
              margin: "0 !important",
              border: "2px solid var(--neutral-100) !important",
            },
            "& .Mui-selected": {
              border: "2px solid transparent !important",
            },
            gap: 2,
          }}
        >
          {options.map(({ value: optionValue, label, icon }) => (
            <ToggleButton
              key={optionValue}
              value={optionValue}
              disableRipple
              sx={{
                borderRadius: "48px",
                position: "relative",
                border:
                  value === optionValue
                    ? "2px solid transparent"
                    : "2px solid var(--neutral-100)",
                background:
                  value === optionValue
                    ? "linear-gradient(white, white) padding-box, linear-gradient(114.44deg, #6366F1 0%, #9F03FF 100%) border-box"
                    : "none",
                backgroundColor: value === optionValue ? "transparent" : "#fff",
                color: "var(--neutral-800)",
                fontWeight: 400,
                whiteSpace: "nowrap",
                fontSize: isMobile ? "12px" : "15px",
                textTransform: "none",
                px: isMobile ? 2 : 3,
                py: 1.2,
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: "var(--primary-100)",
                  borderRadius: "48px",
                  borderColor: "var(--primary-200)",
                },
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {icon}
                {label}
              </span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default GradientToggleButtonGroup;
